import type { AttendanceMe } from '@erp/shared';
import { Fingerprint, LogIn, LogOut } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, Skeleton } from '../../../components/ui/index.js';
import { usePermission } from '../../../hooks/usePermission.js';
import { attendanceErrorCode, clockSelf, formatMinutes, getMyAttendance } from '../../../lib/attendance.js';

import { AttendanceStatusBadge } from './AttendanceStatusBadge.js';

/** `HH:MM` plus whole minutes, wrapping at midnight. */
function addClock(clock: string, minutes: number): string {
  const [h, m] = clock.split(':').map(Number) as [number, number];
  const total = (((h * 60 + m + minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * "Clock in / Clock out" for the signed-in person.
 *
 * The shop's clock ticks here locally from the `now` the API sent, rather than
 * from the browser's own time: a phone set to the wrong zone must not show
 * the wrong hour next to a button that records the right one.
 *
 * `compact` is the dashboard version: it renders nothing at all for someone
 * with no employee record (or one who is not tracked) instead of explaining why.
 */
export function AttendanceClockCard({ compact = false, onChange }: { compact?: boolean; onChange?: () => void }): JSX.Element | null {
  const { t } = useTranslation('hr');
  const canClock = usePermission('hr.self.time.entry');
  const [me, setMe] = useState<AttendanceMe | null>(null);
  const [missing, setMissing] = useState(false);
  const [loadedAt, setLoadedAt] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const reload = useCallback(async (): Promise<void> => {
    try {
      const data = await getMyAttendance();
      setMe(data);
      setLoadedAt(Date.now());
      setMissing(false);
    } catch {
      setMissing(true);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (missing || (me && !me.employee.trackAttendance)) {
    if (compact) return null;
    return (
      <Card className="flex items-center gap-3 p-4 text-sm text-fg-muted">
        <Fingerprint className="h-5 w-5 shrink-0" aria-hidden />
        {missing ? t('attendance.errors.no_employee_for_user') : t('attendance.clock.notTracked')}
      </Card>
    );
  }
  if (!me) {
    if (compact) return null;
    return <Card className="p-5"><Skeleton className="h-12 w-full" /></Card>;
  }

  const elapsed = Math.max(0, Math.floor((Date.now() - loadedAt) / 60_000));
  const shopNow = addClock(me.now.slice(11), elapsed);
  const open = me.openSession;
  const worked = me.today.day.workedMinutes + (me.today.day.open ? elapsed : 0);
  const totals = me.month.totals;

  const punch = async (): Promise<void> => {
    setBusy(true);
    try {
      const r = await clockSelf(open ? 'out' : 'in');
      const time = (r.action === 'in' ? r.record.checkIn : r.record.checkOut ?? r.record.checkIn).slice(11);
      toast.success(t(r.action === 'in' ? 'attendance.clock.clockedIn' : 'attendance.clock.clockedOut', { time }));
      await reload();
      onChange?.();
    } catch (err) {
      const code = attendanceErrorCode(err);
      toast.error(t(`attendance.errors.${code ?? 'generic'}`, { defaultValue: t('attendance.errors.generic') }));
      await reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-4">
        <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${open ? 'bg-bg-subtle text-success ring-1 ring-success' : 'bg-brand-subtle text-brand'}`}>
          <Fingerprint className="h-6 w-6" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-fg-muted">{t('attendance.clock.hello', { name: me.employee.displayName })}</div>
          <div className="text-lg font-semibold">
            {open ? t('attendance.clock.clockedInSince', { time: open.checkIn.slice(11) }) : t('attendance.clock.notClockedIn')}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
            <span>{t('attendance.board.shopTime', { time: shopNow })}</span>
            <span>
              {t('attendance.clock.todayWorked')}{' '}
              <span className="font-mono tabular-nums text-fg">{formatMinutes(worked)}</span>
            </span>
            {me.today.sessions.length > 0 && <AttendanceStatusBadge day={me.today.day} />}
          </div>
        </div>
        {canClock && me.selfServiceEnabled ? (
          <Button size="lg" variant={open ? 'secondary' : 'primary'} loading={busy} onClick={() => void punch()} className="gap-2">
            {open ? <LogOut className="h-4 w-4 rtl:rotate-180" aria-hidden /> : <LogIn className="h-4 w-4 rtl:rotate-180" aria-hidden />}
            {open ? t('attendance.clock.clockOut') : t('attendance.clock.clockIn')}
          </Button>
        ) : (
          canClock && <p className="max-w-xs text-xs text-fg-muted">{t('attendance.clock.selfServiceOff')}</p>
        )}
      </div>
      {!compact && (
        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-4 md:grid-cols-4">
          <MonthStat label={t('attendance.clock.presentDays')} value={String(totals.presentDays)} />
          <MonthStat label={t('attendance.clock.absentDays')} value={String(totals.absentDays)} danger={totals.absentDays > 0} />
          <MonthStat label={t('attendance.clock.lateMinutes')} value={formatMinutes(totals.lateMinutes)} danger={totals.lateMinutes > 0} />
          <MonthStat label={t('attendance.clock.overtime')} value={formatMinutes(totals.overtimeMinutes)} />
          <p className="col-span-2 text-[0.7rem] text-fg-subtle md:col-span-4">
            {t('attendance.clock.thisMonth')} · <span dir="ltr">{me.month.from} – {me.month.to}</span>
          </p>
        </div>
      )}
    </Card>
  );
}

function MonthStat({ label, value, danger = false }: { label: string; value: string; danger?: boolean }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-bg-subtle px-3 py-2">
      <div className="text-xs text-fg-muted">{label}</div>
      <div className={`mt-0.5 font-mono text-lg font-semibold tabular-nums ${danger ? 'text-danger' : ''}`}>{value}</div>
    </div>
  );
}
